import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Loader2,
  BookOpen,
  Upload,
  Image as ImageIcon,
  Building2,
  Check,
  Handshake,
  Star,
  Package,
  CheckCircle2,
  Clock,
  Phone,
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function AlbumTemplatesPage() {
  const { token } = useAuthStore();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [savingCompanies, setSavingCompanies] = useState(false);
  const initialTab = searchParams.get('tab') === 'albumcus' ? 'albumcus'
    : searchParams.get('tab') === 'companies' ? 'companies' : 'own';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Albümcüler (gerçek kullanıcılar - /api/albumcus)
  const [albumcus, setAlbumcus] = useState([]);
  const [loadingAlbumcus, setLoadingAlbumcus] = useState(false);
  const [requestingId, setRequestingId] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadCompanies();
    loadSelectedCompanies();
    loadAlbumcus();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/album-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      toast.error('Şablonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await fetch(`${API}/api/album-companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCompanies(await res.json());
    } catch {
      // silent
    }
  };

  const loadSelectedCompanies = async () => {
    try {
      const res = await fetch(`${API}/api/user/selected-companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedCompanyIds(data.selected_company_ids || []);
      }
    } catch {
      // silent
    }
  };

  const loadAlbumcus = async () => {
    setLoadingAlbumcus(true);
    try {
      const res = await fetch(`${API}/api/albumcus`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAlbumcus(await res.json());
    } catch {
      // silent
    } finally {
      setLoadingAlbumcus(false);
    }
  };

  const requestPartnership = async (a) => {
    setRequestingId(a.id);
    try {
      const r = await fetch(`${API}/api/albumcu-partnerships/request/${a.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await r.json();
      if (data.already) {
        toast.info(data.message || 'Zaten talep var');
      } else {
        toast.success(`${a.company_name || a.full_name} için anlaşma talebi gönderildi.`);
      }
      loadAlbumcus();
    } catch {
      toast.error('Talep gönderilemedi');
    } finally {
      setRequestingId(null);
    }
  };

  const endPartnership = async (a) => {
    if (!window.confirm(`${a.company_name || a.full_name} ile anlaşmayı sonlandırmak istediğinize emin misiniz? Bundan sonra siparişlerinizde bu albümcü görünmeyecek.`)) return;
    try {
      // Önce mevcut anlaşma id'sini bul
      const r = await fetch(`${API}/api/albumcu-partnerships/mine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const mine = await r.json();
      const p = (mine || []).find(x => x.albumcu_id === a.id);
      if (!p) return toast.error('Anlaşma bulunamadı');
      await fetch(`${API}/api/albumcu-partnerships/${p.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Anlaşma sonlandırıldı');
      loadAlbumcus();
    } catch { toast.error('İşlem başarısız'); }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const saveCompanySelection = async () => {
    setSavingCompanies(true);
    try {
      const res = await fetch(`${API}/api/user/selected-companies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ company_ids: selectedCompanyIds })
      });
      if (!res.ok) throw new Error();
      toast.success('Firma seçimleri kaydedildi');
    } catch {
      toast.error('Kayıt başarısız');
    } finally {
      setSavingCompanies(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Şablon adı gerekli');
      return;
    }
    
    if (!selectedImage) {
      toast.error('Lütfen bir resim seçin');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('image', selectedImage);
      
      const res = await fetch(`${API}/api/album-templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      
      if (!res.ok) throw new Error('Şablon eklenemedi');
      
      toast.success('Albüm şablonu eklendi');
      setIsDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`"${templateName}" şablonunu silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/album-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Şablon silinemedi');
      
      toast.success('Şablon silindi');
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="album-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Albüm Şablonları</h1>
          <p className="text-muted-foreground mt-1">
            Kendi şablonlarınızı ekleyin veya firma modellerini seçin
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveTab('own')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'own'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          data-testid="own-templates-tab"
        >
          Kendi Şablonlarım ({templates.length})
        </button>
        <button
          onClick={() => setActiveTab('companies')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'companies'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          data-testid="company-templates-tab"
        >
          <span className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Firma Modelleri ({companies.length})
          </span>
        </button>
        <button
          onClick={() => setActiveTab('albumcus')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'albumcus'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          data-testid="albumcus-partnership-tab"
        >
          <span className="flex items-center gap-2">
            <Handshake className="h-4 w-4" />
            Albümcüler & Anlaşma ({albumcus.filter(a => a.is_partner).length}/{albumcus.length})
          </span>
        </button>
      </div>

      {/* Own Templates Tab */}
      {activeTab === 'own' && (
        <>
          <div className="flex justify-end">
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-template-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Yeni Şablon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Yeni Albüm Şablonu</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Şablon Adı *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Örn: Klasik Beyaz, Modern Siyah"
                      data-testid="template-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Açıklama</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Şablon hakkında kısa bilgi..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Şablon Görseli *</Label>
                    <div 
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {imagePreview ? (
                        <div className="relative">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="max-h-48 mx-auto rounded"
                          />
                          <p className="text-sm text-muted-foreground mt-2">
                            Değiştirmek için tıklayın
                          </p>
                        </div>
                      ) : (
                        <div className="py-4">
                          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Resim yüklemek için tıklayın
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)} 
                      className="flex-1"
                    >
                      İptal
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={isSubmitting} 
                      className="flex-1"
                      data-testid="template-submit-btn"
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : templates.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="overflow-hidden" data-testid={`template-card-${template.id}`}>
                  <div className="aspect-[4/3] bg-muted relative group">
                    {template.image_url ? (
                      <img
                        src={`${API}${template.image_url}`}
                        alt={template.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(template.id, template.name)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Sil
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold">{template.name}</h3>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">Henüz şablon yok</h3>
                <p className="text-muted-foreground mb-4">
                  Müşterilerinizin seçebileceği albüm kapak görselleri ekleyin
                </p>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Şablonu Ekle
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Company Templates Tab */}
      {activeTab === 'companies' && (
        <div className="space-y-4">
          <div className="bg-muted/50 border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-3">
              Çalıştığınız firmaları seçin. Seçtiğiniz firmaların albüm modelleri müşterilerinize otomatik olarak gösterilecek.
            </p>
            {selectedCompanyIds.length > 0 && (
              <Button
                onClick={saveCompanySelection}
                disabled={savingCompanies}
                size="sm"
                data-testid="save-company-selection-btn"
              >
                {savingCompanies ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Seçimleri Kaydet
              </Button>
            )}
          </div>

          {companies.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">Henüz firma eklenmemiş</h3>
                <p className="text-muted-foreground">
                  Admin tarafından albüm firmaları eklendiğinde burada görünecek
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {companies.map((company) => {
                const isSelected = selectedCompanyIds.includes(company.id);
                return (
                  <Card
                    key={company.id}
                    className={`overflow-hidden transition-all cursor-pointer ${
                      isSelected ? 'ring-2 ring-primary' : 'hover:shadow-md'
                    }`}
                    onClick={() => toggleCompany(company.id)}
                    data-testid={`company-select-${company.id}`}
                  >
                    <div className="flex items-center gap-4 p-4">
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                      }`}>
                        {isSelected && <Check className="h-4 w-4 text-primary-foreground" />}
                      </div>
                      
                      {company.logo_url ? (
                        <img
                          src={`${API}${company.logo_url}`}
                          alt={company.name}
                          className="h-12 w-12 rounded-lg object-cover border flex-shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold">{company.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {company.templates?.length || 0} model
                          {company.contact_info && ` | ${company.contact_info}`}
                        </p>
                      </div>
                    </div>

                    {isSelected && company.templates?.length > 0 && (
                      <div className="border-t px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                          {company.templates.map((t) => (
                            <div key={t.id} className="aspect-[4/3] rounded overflow-hidden border">
                              {t.image_url ? (
                                <img src={`${API}${t.image_url}`} alt={t.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-muted">
                                  <ImageIcon className="h-4 w-4 text-muted-foreground/30" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
              
              {selectedCompanyIds.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    onClick={saveCompanySelection}
                    disabled={savingCompanies}
                    data-testid="save-company-selection-btn-bottom"
                  >
                    {savingCompanies ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Seçimleri Kaydet ({selectedCompanyIds.length} firma)
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Albümcüler & Anlaşma Tab — gerçek albümcü kullanıcıları */}
      {activeTab === 'albumcus' && (
        <div className="space-y-4" data-testid="albumcus-partnership-content">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Handshake className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Anlaşma sistemi nasıl çalışır?</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Çalışmak istediğiniz albümcüye <b>"Anlaşma Yap"</b> talebi gönderin.</li>
                  <li>Albümcü onay verdikten sonra fiyatları ve tüm ürünleri görebilirsiniz.</li>
                  <li>Anlaşmalı albümcüleriniz müşteri kartında <b>"Albüm İçin Gönder"</b> akışında görünür.</li>
                </ol>
              </div>
            </div>
          </div>

          {loadingAlbumcus ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : albumcus.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Handshake className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">Henüz kayıtlı albümcü yok</h3>
                <p className="text-muted-foreground text-sm">
                  Albümcüler sisteme kayıt olduğunda burada görünecek.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {albumcus.map((a) => {
                const isPartner = a.is_partner;
                const isPending = a.partnership_status === 'pending';
                return (
                  <Card key={a.id}
                    className={`transition ${isPartner ? 'ring-2 ring-emerald-500/40 bg-emerald-50/30' : 'hover:shadow-md'}`}
                    data-testid={`albumcu-partnership-card-${a.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Package className="h-4 w-4 text-primary flex-shrink-0" />
                            <h3 className="font-bold truncate">{a.company_name || a.full_name}</h3>
                            {isPartner && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                <CheckCircle2 className="h-3 w-3" />Anlaşmalı
                              </span>
                            )}
                            {isPending && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">
                                <Clock className="h-3 w-3" />Bekliyor
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{a.full_name}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {a.rating > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                            {a.rating.toFixed(1)}
                            <span className="text-slate-400 font-normal">({a.review_count})</span>
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Package className="h-3.5 w-3.5" />
                          {a.product_count || 0} ürün
                        </span>
                        {a.completed_orders > 0 && (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {a.completed_orders} tamamlanan
                          </span>
                        )}
                        {a.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {a.phone}
                          </span>
                        )}
                      </div>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                        {isPartner ? (
                          <Button size="sm" variant="outline"
                            onClick={() => endPartnership(a)}
                            data-testid={`end-partnership-${a.id}`}
                            className="text-red-600 border-red-200 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Anlaşmayı Sonlandır
                          </Button>
                        ) : isPending ? (
                          <Button size="sm" variant="outline" disabled>
                            <Clock className="h-3.5 w-3.5 mr-1" />
                            Onay Bekliyor
                          </Button>
                        ) : (
                          <Button size="sm"
                            disabled={requestingId === a.id}
                            onClick={() => requestPartnership(a)}
                            data-testid={`request-partnership-${a.id}`}
                            className="bg-primary hover:bg-primary/90">
                            {requestingId === a.id
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <Handshake className="h-3.5 w-3.5 mr-1" />}
                            Anlaşma Yap
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
